import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Box } from "@mui/material";
import CODE from "../../Static/Constants/StatusCodes";
import { ThemeProperties } from "../../Theme/ThemeProperties";
import Loader from "../../Helper/Loaders/Loader";
import DigitalTwinMain from "./DigitalTwinMain";
import {
	decryptTheParams,
} from "../../Helper/QueryParams/EncryptDecrypt";
import { getBatteryHierarchyV3, getRealTimeInfo } from "../../Api/DigitalTwin";

import Events from "../../Analytics/Events";
import { getPagesMetaData } from "../../Api/Api";
import { useTranslation } from "react-i18next";
import ScreenContainer from "../../Components/ScreenContainer";
import { transformTwinHierarchyData } from "../../Helper/DigitalTwin/DigitalTwinFunctions";
import { DigitalTwinProvider } from './DigitalTwinContext';
import { getVehicleInfo } from "../../Api/VehicleApi";
import { shouldShowVehicleIdentifier } from "../../Helper/DigitalTwin/vehicleIdentifier";


function DigitalTwin() {
	const queryParams = decryptTheParams();
	const batteryID = queryParams?.batteryID;
	const timezoneChanged = useSelector((state) => state.TimeZoneChanged.value);
	const { t } = useTranslation(['digital_twin'])
	const [batteryInfo, setBatteryInfo] = React.useState({})
	const [batteryConfig, setBatteryConfig] = React.useState({})
	const [batteryStructure, setBatteryStructure] = React.useState([])
	const [batterySystem, setBatterySystem] = React.useState([])
	const [vehicleInfo, setVehicleInfo] = React.useState({})
	const [batteryInfoFetched, setBatteryInfoFetched] = React.useState(false)
	// Bumped when the operational status is updated (from the header modal) so
	// dependent views (e.g. the Components battery grid) can re-read it.
	const [operationalStatusVersion, setOperationalStatusVersion] = React.useState(0)
	const refreshOperationalStatus = React.useCallback(
		() => setOperationalStatusVersion((v) => v + 1),
		[]
	)

	const [pagesContent, setPagesContent] = useState({
		batteryID: "",
		partner: "",
		tabValue: 0,
		pageHeader: "",
		mounted: false,
	});

	const fetchDigitalTwinHierarchy = async () => {
		if (!batteryID) {
			console.error("DigitalTwin: batteryID is missing from query params");
			setBatteryInfoFetched(true);
			return;
		}
		try {
			const [hierarchyRes, realTimeRes, vehicleInfoRes] = await Promise.all([
				getBatteryHierarchyV3(batteryID),
				getRealTimeInfo(batteryID),
				getVehicleInfo(batteryID),
			]);

			const { BATTERY_STRUCTURE: batteryStructure, batteryConfig, BATTERY_SYSTEM: batterySystem } = hierarchyRes.response;
			const realTimeInfo = realTimeRes.data;

			const formattedStructure = transformTwinHierarchyData(batteryStructure, t);
			const formattedSystemContent = transformTwinHierarchyData(batterySystem, t);

			setBatteryInfo(realTimeInfo || {});
			setBatteryConfig(batteryConfig || {});
			setBatteryStructure(formattedStructure);
			setBatterySystem(formattedSystemContent);
			setBatteryInfoFetched(true)
			setVehicleInfo(vehicleInfoRes?.response?.data || {});
		} catch (error) {
			console.error('Error fetching digital twin hierarchy:', error);
			setBatteryInfoFetched(true)
		}
	};

	// Identify the asset by its licence plate: from globalConfig.baseEntity when the
	// org sets a type, else the legacy application === 'bus' rule.
	const showBusDeviceInfo = () => shouldShowVehicleIdentifier({
		globalConfig: pagesContent?.metaInfo?.globalConfig,
		batteryConfig,
		licensePlateNo: vehicleInfo?.licensePlateNo,
	})

	async function getPageMeta() {
		const pageName = queryParams?.pageType === 'IOTDigitalTwin' ? 'IOTDigitalTwin' : 'digitalTwin'
		const res = await getPagesMetaData(pageName)
		Events("Digital Twin page opened for particular battery ID", {
			batteryID: batteryID
		})

		if (res && res?.responseStatus?.code === CODE.SUCCESS) {
			const pageData = res.response?.response?.metaInfo

			setPagesContent({
				...pagesContent,
				metaInfo: pageData,
				mounted: true
			})
		}
	}

	useEffect(() => { getPageMeta(); }, []);

	useEffect(() => {
		if (pagesContent.mounted) {
			fetchDigitalTwinHierarchy()
		}
	}, [pagesContent.mounted, timezoneChanged])

	if (!pagesContent.mounted || !batteryInfoFetched) {
		return (
			<Box sx={{ height: "100vh", bgcolor: ThemeProperties.backgroundPurpleDarker }}>
				<Loader />
			</Box>
		)
	}

	return (
		<ScreenContainer pagesContent={pagesContent} key={timezoneChanged} >
			<DigitalTwinProvider value={{
				batteryID,
				batteryInfo,
				setBatteryInfo,
				batteryConfig,
				batteryStructure,
				batterySystem,
				showBusDeviceInfo,
				vehicleInfo,
				setVehicleInfo,
				operationalStatusVersion,
				refreshOperationalStatus,
				pageType: queryParams?.pageType,
			}}>
				<DigitalTwinMain
					pagesContent={pagesContent}
					batteryID={batteryID}
				/>
			</DigitalTwinProvider>
		</ScreenContainer>
	);
}

export default DigitalTwin;
